import { useCallback, useEffect, useState } from 'react';
import type { ChatMessage, StationSnapshot } from './types';
import { api } from './api';
import { wsBus } from './wsBus';

const EMPTY: StationSnapshot = { status: 'idle', nowPlaying: null, queue: [], playlist: [], current: -1 };

export function useStation() {
  const [snap, setSnap] = useState<StationSnapshot>(EMPTY);
  const [connected, setConnected] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [working, setWorking] = useState(false);
  const [statusLabel, setStatusLabel] = useState('');

  const pushUser = useCallback((text: string) => {
    setMessages((prev) => [...prev, { role: 'user', text, ts: Date.now() }]);
  }, []);

  useEffect(() => {
    // token-aware: on 401 these throw and we keep the safe EMPTY default (no poisoned snap)
    api
      .now()
      .then((s) =>
        setSnap({
          status: s.status,
          nowPlaying: s.nowPlaying,
          queue: s.queue ?? [],
          playlist: s.playlist ?? [],
          current: s.current ?? -1,
          modePreparing: s.modePreparing,
          driving: s.driving,
          reading: s.reading,
          listeners: s.listeners,
        }),
      )
      .catch(() => {});
    api
      .messages()
      .then((d) => {
        const parsed = (d.messages ?? []).map((m) => ({
          ...m,
          ts: m.ts ? new Date(m.ts).getTime() : undefined,
        }));
        setMessages((prev) => {
          if (!prev.length) return parsed;
          const inHistory = (m: ChatMessage) => parsed.some((p) => p.role === m.role && p.text === m.text);
          return [...parsed, ...prev.filter((m) => !inHistory(m))];
        });
      })
      .catch(() => {})
      .finally(() => setHistoryLoaded(true));

    const offStatus = wsBus.subscribeStatus(setConnected);
    const off = wsBus.subscribe((m) => {
      if (m.type === 'state') {
        setSnap({
          status: m.status,
          nowPlaying: m.nowPlaying,
          queue: m.queue ?? [],
          playlist: m.playlist ?? [],
          current: m.current ?? -1,
          modePreparing: m.modePreparing,
          driving: m.driving,
          reading: m.reading,
          listeners: m.listeners,
        });
      } else if (m.type === 'dj' && (m.say || m.remembered?.length)) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'dj',
            text: m.say || '',
            reason: m.reason,
            remembered: m.remembered ?? [],
            ts: m.ts ? new Date(m.ts).getTime() : Date.now(),
          },
        ]);
      } else if (m.type === 'status') {
        setWorking(!!m.working);
        setStatusLabel(m.working ? m.label || '' : '');
      }
    });

    return () => {
      off();
      offStatus();
    };
  }, []);

  return { snap, connected, messages, historyLoaded, pushUser, working, statusLabel };
}
